<template>
  <div
    class="recommendation-card"
    :class="`${productCardIndex === 0 ? 'green-border' : ''} ${productCardIndex === 1 ? 'blue-border middle-card' : ''} ${productCardIndex === 2 ? 'deep-green-border' : ''}`"
  >
    <div class="spacer"></div>
    <section class="quote-header">
      <div
        class="promotional-tag"
        :class="`${productCardIndex === 0 && productPromotionalTag ? 'green' : ''} ${productCardIndex === 1 && productPromotionalTag ? 'blue' : ''} ${
          isCurrentCover && refData.recommendationsCurrentCoverSubheading.informationText ? 'deep-green' : ''
        }`"
      >
        <p v-if="!isCurrentCover">{{ productPromotionalTag }}</p>
        <p v-else>{{ refData.recommendationsCurrentCoverSubheading.informationText }}</p>
      </div>
      <div
        ref="recommendationCardHeading"
        class="recommendation-card-heading"
        :style="{ height: allHeightsCaptured && currentDevice !== DeviceTypes.MOBILE ? `${sectionHeights.headingMaxHeight}px` : '' }"
      >
        <h2 class="h4">{{ recommendation.recommendationName }}</h2>
        <p>{{ toSentenceCase(summaryCoverComposition(recommendation.composition)) }}</p>
        <p v-if="recommendation.closedProductRetain" class="closed-product-retain">
          <span>{{ toSentenceCase(recommendation.closedProductRetain) }}</span>
        </p>
      </div>
      <div class="recommendation-price">
        <span>{{ convertToCurrencyString(recommendation.price) }}</span>
        / {{ paymentFrequency }}*
      </div>
      <div class="cover-selection-wrapper">
        <a
          v-if="isCurrentCover"
          id="keep-cover-btn"
          href="#"
          class="button"
          role="button"
          aria-label="keep this cover and redirect to the keep cover page"
          @click.prevent="keepCover(convertToCurrencyString(recommendation.price))"
        >
          {{ refData.recommendationsKeepProductButtonLabel.informationText }}
        </a>
        <template v-else>
          <a
            id="select-cover-btn"
            href="#"
            class="button"
            role="button"
            aria-label="select this cover and redirect to the selected cover page"
            @click.prevent="changeCover(recommendation)"
          >
            {{ refData.recommendationsSelectProductButtonLabel.informationText }}
          </a>
          <a
            href="#"
            id="email-quote-btn"
            class="secondary button"
            role="button"
            aria-label="email me this quote"
            @click.prevent="$emit('emailQuote', recommendation, selectedExcess)"
            v-if="!isCrm"
          >
            Email me this quote
          </a>
        </template>
      </div>
    </section>
    <div
      ref="discountsAndLoadings"
      class="discounts fr-view"
      :style="{ height: allHeightsCaptured && currentDevice !== DeviceTypes.MOBILE ? `${sectionHeights.discountsAndLoadingsMaxHeight}px` : '' }"
    >
      <div class="description" v-html="refData.recommendationsInformationText.informationText"></div>
      <p id="disc-load-subheading">Discounts applied</p>
      <ul>
        <li v-for="discount in adjustments.discounts" :key="`${discount.category}`">
          <span class="adjustment-title">{{ stringifyDiscountCategory(discount.category) }}</span>
          <!-- <span v-if="isRebateCategory(discount.category)" class="adjustment-percentage">{{ formatPercentageString(discount.percentage, 3) }}</span> -->
          <!-- <span v-else class="adjustment-percentage">{{ formatPercentageString(discount.percentage, 0) }}</span> -->
        </li>
      </ul>
    </div>
    <div class="loadings fr-view">
      <p id="disc-load-subheading">Loadings applied</p>
      <div class="description-small" v-html="refData.recommendationsLoadingsInformationText.informationText"></div>
      <div v-if="!isCrm" class="description-small" v-html="refData.recommendationsLoadingsInformationTextMP.informationText"></div>
      <ul>
        <li v-for="loading in adjustments.loadings" :key="`${loading.category}`">
          <span class="adjustment-title">{{ stringifyDiscountCategory(loading.category) }}</span>
          <span v-if="isLhcSurchargeCategory" class="adjustment-percentage">{{ formatPercentageString(loading.percentage, 0) }}</span>
        </li>
      </ul>
    </div>
    <div
      ref="productDescriptions"
      class="product-descriptions"
      :style="{ height: allHeightsCaptured && currentDevice !== DeviceTypes.MOBILE ? `${sectionHeights.productDescriptionsMaxHeight}px` : '' }"
    >
      <p v-for="(product, i) in recommendation.products" :key="`product-description-${i}`">{{ product.productDescription }}</p>
    </div>
    <div class="frequency-wrapper">
      <label>Payment frequency</label>
      <DropdownSelect :items="[paymentFrequency]" :initialSelect="paymentFrequency" :disabled="true" />
    </div>
    <div class="excess-wrapper" v-if="renderExcessDropdown">
      <label :for="`quote-excess`">{{ refData.recommendationsHospitalExcessLabel.informationText }}</label>
      <DropdownSelect
        :class="`quote-excess`"
        :items="findProductExcess(recommendation) ? convertToDropDownItemList(findProductExcess(recommendation)) : [{ name: 'N/A' }]"
        :disabled="!findProductExcess(recommendation) || (recommendation.closedProductRetain && recommendation.disableExcess) ? true : false || isCurrentCover"
        :initialSelect="!findProductExcess(recommendation) ? 'N/A' : selectedExcess"
        v-model="selectedExcess"
        :noDisabled="true"
        prefix="$"
      />
    </div>
    <div class="accordions">
      <Accordion
        v-if="showHospitalAccordion"
        accBtn="Hospital"
        id="hospital-features"
        :isOpen="accordionStates.hospital.main"
        @update:accordionState="(state) => handleAccordionStateChange(state, 'main', 'hospital')"
        accordionClass="secondary no-padding"
        :class="{ 'no-bottom-border': accordionStates.hospital.main }"
      >
        <Accordion
          v-for="(hospitalNeed, index) in hospitalAccordionContent"
          :key="index"
          :accBtn="hospitalNeed.propertyName"
          :id="`hospital-features-${index}`"
          :iconUrl="getIconUrl(hospitalNeed.propertyValue, isCrm)"
          :isOpen="getAccordionStateId(hospitalNeed, 'hospital')"
          :isCrm="isCrm"
          @update:accordionState="(state) => handleAccordionStateChange(state, hospitalNeed.propertyName, 'hospital')"
          accordionClass="secondary with-icon no-padding"
        >
          <div :data-index="index" class="hospital-description-wrapper">
            <p>{{ hospitalNeed.qualifyingStatements }}</p>
          </div>
        </Accordion>
      </Accordion>
      <Accordion
        v-if="showExtrasAccordion"
        accBtn="Extras"
        id="extras-features"
        :isOpen="accordionStates.extras.main"
        @update:accordionState="(state) => handleAccordionStateChange(state, 'main', 'extras')"
        accordionClass="secondary"
        :class="{ 'no-bottom-border': accordionStates.extras.main }"
      >
        <Accordion
          v-for="(extrasNeed, index) in extrasAccordionContent"
          :key="index"
          :accBtn="extrasNeed.propertyName"
          :id="`extras-features-${index}`"
          :iconUrl="getIconUrl(extrasNeed.propertyValue, isCrm)"
          :isOpen="getAccordionStateId(extrasNeed, 'extras')"
          :isCrm="isCrm"
          @update:accordionState="(state) => handleAccordionStateChange(state, extrasNeed.propertyName, 'extras')"
          accordionClass="secondary with-icon"
        >
          <div :data-index="index" class="extras-description-wrapper">
            <template v-if="extrasNeed.flexiLimit">
              <p>{{ extrasNeed.qualifyingStatements }}</p>
              <p>
                <strong>Annual Limit (included with Flexi-limit)</strong>
                <br />
                {{ flexiLimitValue.propertyValue }}
              </p>
              <p>
                Combined annual limit for {{ flexiLimitNames.join(', ') }}
                <br />
                {{ flexiLimitValue.qualifyingStatements }}
              </p>
            </template>
            <template v-else-if="extrasNeed.propertyValue.toLowerCase().includes('sub limits apply')">
              <p>{{ extrasNeed.qualifyingStatements }}</p>
              <p>
                <strong>Annual Limit</strong>
                <br />
                {{ extrasNeed.propertyValue }}
              </p>
              <p>
                {{ copy.FLEXILIMITS_SUBLIMITS_INFORMATION }}
              </p>
            </template>
            <template v-else-if="extrasNeed.propertyValue[0] === '$'">
              <p>{{ extrasNeed.qualifyingStatements }}</p>
              <p>
                <strong>Annual Limit</strong>
                <br />
                {{ extrasNeed.propertyValue }}
              </p>
            </template>
            <template v-else>
              <p>{{ extrasNeed.qualifyingStatements }}</p>
              <p>{{ extrasNeed.propertyValue }}</p>
            </template>
          </div>
        </Accordion>
      </Accordion>
    </div>
    <div class="guide-links">
      <a v-for="(productGuide, i) in productGuideInfo" :key="`link-${i}`" rel="external" download :href="productGuide.guideUrl" target="_blank">
        Download {{ productGuide.name }} product guide
      </a>
    </div>
  </div>
</template>

<script>
import Accordion from '@/components/Accordion/Accordion.vue';
import { getCurrentDeviceLayout } from '@/utils';
import DeviceTypes from '@/enums/DeviceTypes';
import { convertToCurrencyString, formatPercentageString, stringifyDiscountCategory, summaryCoverComposition, toSentenceCase } from '@general/js/utils';
import { ProductType, DiscountCategoriesJSON, DiscountsList, LoadingsList } from '@general/js/enums';
import copy from '@general/js/constants/Copy';
import DropdownSelect from '@general/js/components/DropdownSelect.vue';
import { KEEP_MY_COVER } from '../router/routeNames';
import { setNavigationState } from '../router/navigationState';

const EXTRAS_PRODUCT_TYPE = 'Extras';

export default {
  name: 'RecommendedProductCard',

  components: {
    Accordion,
    DropdownSelect,
  },

  props: {
    // Captures whether the heights of all variable height content in this component has been received in the parent,
    // so that the max height can be used to align content
    allHeightsCaptured: {
      type: Boolean,
    },

    // This is to be passed in as true when passing in "My cover" quote(i.e current cover recommendation/quote) in Mobile Quotes version Change of Cover.
    isCurrentCover: {
      type: Boolean,
      default: false,
    },

    // Can be one of: Fortnightly, Monthly, Quarterly etc
    paymentFrequency: {
      type: String,
      required: true,
    },

    // Links to the guides for each product, it is an array as a product may have multiple guides
    productGuideInfo: {
      type: Array,
      required: true,
    },

    // Refers to the relative order this component is shown in the parent
    productCardIndex: {
      type: Number,
      required: true,
    },

    // Content that will be displayed in the tag at the top of the card
    productPromotionalTag: {
      type: String,
      default: '',
    },

    isCrm: {
      type: Boolean,
      default: false,
    },

    accordionStates: {
      type: Object,
      required: false,
      default: () => {
        return {
          hospital: {},
          extras: {},
        };
      },
    },

    /**
     * Recommendation object
     * The object contains the following properties:
     * - recommendationName: name of recommendation
     * - composition: cover composition
     * - productDescription: description of product recommendation
     * - price: price of recommendation
     * - frequency: frequency of payment
     * - closedProductRetain: text for information about close product (optional)
     * - disableExcess: boolean for disabling excess dropdown
     * - products: list of products (objects) for the recommendation. Each product contains
     *      - productType: type of product
     *      - productExcess: list of excess values (if any)
     */
    recommendation: {
      type: Object,
      required: true,
    },

    refData: {
      type: Object,
      default: () => {
        return {
          recommendationsButtonLabel: {
            informationText: 'See my recommendations',
          },
          recommendationsSubheading: {
            informationText: 'My recommendations',
          },
          recommendationsCurrentCoverSubheading: {
            informationText: 'My cover',
          },
          recommendationsInformationText: {
            informationText: '<p>The premium quoted, including any discounts and loadings, is based on the information you have provided as at today’s date.</p>\r\n',
          },
          recommendationsHospitalExcessLabel: {
            informationText: 'Hospital excess',
          },
          recommendationsSelectProductButtonLabel: {
            informationText: 'Select this cover',
          },
          recommendationsKeepProductButtonLabel: {
            informationText: 'Keep my cover',
          },
          recommendationsLoadingsInformationText: {
            informationText:
              '<p>Loadings increase your premium based on when you first took out hospital cover. See the <a href="https://www.ato.gov.au/individuals-and-families/medicare-and-private-health-insurance/private-health-insurance-rebate/lifetime-health-cover" target="_blank">ATO article on LHC</a> for more information.</p>',
          },
          recommendationsLoadingsInformationTextMP: {
            informationText: '<p>If LHC loading looks inaccurate, contact us for a more accurate quote. Alternatively, loading will be calculated correctly upon cover change.</p>',
          },
        };
      },

      isMemberPortal: {
        type: Boolean,
        default: false,
      },
    },

    // The heights that the non-fixed height sections of the card should be set to
    sectionHeights: {
      type: Object,
    },

    /**
     * Currently selected excess values to be displayed
     */
    selectedExcessValue: {
      type: String,
      required: false,
    },

    stickyHeaderGap: {
      type: Number,
      required: false,
      default: 24,
    },
  },

  data() {
    return {
      equalisedHeightFeatureSections: ['.hospital-description-wrapper', '.extras-description-wrapper', '.cover-selection-wrapper'],
      equalisedHeightSections: ['.discounts', '.loadings'],
      currentDevice: DeviceTypes.DESKTOP,
      DeviceTypes,
      selectedExcess: null,
      copy,
      adjustments: [],
    };
  },

  beforeMount() {
    this.getExcessValue();
    this.resizeHandler();
  },

  beforeUnmount() {
    window.removeEventListener('scroll', this.handleScroll);
  },

  mounted() {
    this.$emit('sectionHeightUpdated', {
      headingHeight: this.$refs.recommendationCardHeading?.clientHeight,
      productDescriptionHeight: this.$refs.productDescriptions?.clientHeight,
      discountsAndLoadingsHeight: this.$refs.discountsAndLoadings?.clientHeight,
    });

    window.addEventListener('resize', this.resizeHandler);
    window.addEventListener('scroll', this.handleScroll);
    this.equaliseHeights(true, this.equalisedHeightSections);
    this.equaliseHeightsByIndex(true, this.equalisedHeightFeatureSections);

    this.adjustments = this.getDiscountsAndLoadingsList(this.recommendation.discountsAndLoadings);
  },

  unmounted() {
    window.removeEventListener('resize', this.resizeHandler);
  },

  computed: {
    renderExcessDropdown() {
      return !!this.recommendation.products.find((product) => {
        return product.productType !== EXTRAS_PRODUCT_TYPE;
      });
    },

    productProperties() {
      return this.recommendation.products.flatMap((product) => {
        return product.productProperties;
      });
    },

    extrasAccordionContent() {
      return this.productProperties.filter((property) => {
        // Only show extras features that are included and not part of the flexi-limit
        return (
          property.propertyType.toLowerCase() === 'extras' &&
          property.propertyName.toLowerCase() !== 'flexi-limit' &&
          property.propertyValue !== 'Included w/ Flexi-limit' &&
          !(property.propertyValue === 'Excluded' && property.flexiLimit)
        );
      });
    },

    hospitalAccordionContent() {
      return this.productProperties.filter((property) => {
        return property.propertyType.toLowerCase() === 'hospital';
      });
    },

    flexiLimitValue() {
      const extrasProductProperties = this.productProperties.filter((property) => {
        return property.propertyType.toLowerCase() === 'extras';
      });
      return this.getFeature(extrasProductProperties, 'Flexi-limit');
    },

    flexiLimitNames() {
      const flexiLimitNames = [];

      this.extrasAccordionContent?.forEach((feature) => {
        if (feature.flexiLimit) {
          flexiLimitNames.push(feature.propertyName);
        }
      });

      return flexiLimitNames;
    },

    showHospitalAccordion() {
      return this.hospitalAccordionContent.length > 0;
    },

    showExtrasAccordion() {
      return this.extrasAccordionContent.length > 0;
    },
  },

  methods: {
    changeCover(recommendation) {
      const data = {
        selectedProducts: recommendation,
        selectedExcess: this.selectedExcess,
      };

      this.$emit('changeCover', data);
      window.scrollTo(0, 0); // scroll to top
    },

    convertToDropDownItemList(initialList) {
      return initialList.map((value) => {
        return {
          name: value.toString(),
        };
      });
    },

    /**
     * Find product excess
     * (excess only for hospital and package products)
     */
    findProductExcess(recommendation) {
      for (let i = 0; i < recommendation.products.length; i++) {
        const productType = recommendation.products[i].productType.toLowerCase();
        if (productType === ProductType.HOSPITAL_ONLY || productType === ProductType.PACKAGE) {
          return recommendation.products[i].productExcess;
        }
      }
      return null;
    },

    getDiscountsAndLoadingsList(discountsAndLoadingsObj) {
      if (!discountsAndLoadingsObj) {
        return [];
      }

      const adjustments = { discounts: [], loadings: [] };

      Object.entries(discountsAndLoadingsObj)
        .map(([key, value]) => {
          return { category: key, percentage: value };
        })
        .filter((adjustment) => {
          return adjustment.percentage !== null && adjustment.percentage !== '';
        })
        .forEach((adjustment) => {
          if (DiscountsList.includes(adjustment.category)) {
            adjustments.discounts.push(adjustment);
          } else if (LoadingsList.includes(adjustment.category)) {
            adjustments.loadings.push(adjustment);
          }
        });

      return adjustments;
    },

    getExcessValue() {
      if (this.selectedExcessValue) {
        this.selectedExcess = this.selectedExcessValue;
      } else {
        this.selectedExcess = '0';
      }
    },

    isRebateCategory(category) {
      return category === DiscountCategoriesJSON.REBATE_DISCOUNT;
    },

    isLhcSurchargeCategory(category) {
      return category === DiscountCategoriesJSON.LHC_SURCHARGE;
    },

    keepCover(data) {
      setNavigationState({ currentPremium: data, isFromRecommendationsPage: true });
      this.$router.push({ name: KEEP_MY_COVER });
      window.scrollTo(0, 0); // scroll to top
    },

    resizeHandler() {
      // This is to fetch the current device layout so that appropriate version of my recommendation is shown
      this.currentDevice = getCurrentDeviceLayout(window.innerWidth);
      this.equaliseHeights(this.currentDevice !== DeviceTypes.MOBILE, this.equalisedHeightSections);
    },

    getFeature(features, featureName) {
      let currentFeature = null;
      features.forEach((feature) => {
        if (feature.propertyName.toLowerCase() === featureName.toLowerCase()) {
          currentFeature = feature;
        }
      });
      return currentFeature;
    },

    getAccordionStateId(id, type) {
      return this.accordionStates[type][id?.propertyName];
    },

    equaliseHeightsByIndex(set = true, sections) {
      // nextTick is used to ensure that the DOM has been updated before the height is calculated
      this.$nextTick(() => {
        if (this.gridColumns !== 1) {
          sections.forEach((sectionClass) => {
            const items = document.querySelectorAll(sectionClass);
            const groups = {};

            items.forEach((item) => {
              const index = item.getAttribute('data-index');
              if (!groups[index]) {
                groups[index] = [];
              }
              groups[index].push(item);
            });

            Object.values(groups).forEach((group) => {
              let maxHeight = 0;
              group.forEach((item) => {
                const height = item.offsetHeight;
                if (height > maxHeight) {
                  maxHeight = height;
                }
              });
              group.forEach((item) => {
                if (maxHeight !== 0) {
                  item.style.height = set ? `${maxHeight}px` : '';
                }
              });
            });
          });
        }
      });
    },

    equaliseHeights(set = true, sections) {
      this.$nextTick(() => {
        if (this.gridColumns !== 1) {
          sections.forEach((sectionClass) => {
            const divs = document.querySelectorAll(sectionClass);
            let maxHeight = 0;

            divs.forEach((div) => {
              div.style.height = '';
              const height = div.offsetHeight;
              if (height > maxHeight) {
                maxHeight = height;
              }
            });

            divs.forEach((div) => {
              div.style.height = set ? `${maxHeight}px` : '';
            });
          });
        }
      });
    },

    handleAccordionStateChange(newState, id, type) {
      const newAccordionStates = this.accordionStates;
      newAccordionStates[type][id] = newState;
      this.$emit('update:accordionStatesUpdated', newAccordionStates);
      // Initially set to false to ensure that the heights of all equalisedHeightFeatureSections are reset before applying new calculations
      this.equaliseHeightsByIndex(false, this.equalisedHeightFeatureSections);
      this.equaliseHeightsByIndex(true, this.equalisedHeightFeatureSections);
      this.resizeHandler();
    },

    handleScroll() {
      const headerHeight = document.querySelector('#site-header')?.offsetHeight ?? 0;
      const headerWithGap = headerHeight + this.stickyHeaderGap;
      const quoteCards = document.querySelectorAll('.recommendation-card');

      quoteCards.forEach((card) => {
        const cardTop = card.getBoundingClientRect().top;
        const quoteHeaderBoundings = card.querySelector('.quote-header').getBoundingClientRect();

        if (headerWithGap > cardTop) {
          const spacerForStickyHeader = card.querySelector('.spacer');

          if (!card.classList.contains('sticky-header')) {
            card.classList.add('sticky-header');
            card.querySelector('.quote-header').style.top = this.isCrm ? '24px' : `${headerWithGap}px`;

            spacerForStickyHeader.style.top = `${headerHeight}px`;
            spacerForStickyHeader.style.marginTop = `-${this.isCrm ? 24 : headerHeight}px`;
          } else if (quoteHeaderBoundings.top < headerHeight) {
            // If reaching end of card - need to hide the spacer so it doesn't continue scrolling down the screen
            if (!spacerForStickyHeader.classList.contains('card-end')) {
              spacerForStickyHeader.classList.add('card-end');
            }
          } else if (spacerForStickyHeader.classList.contains('card-end')) {
            spacerForStickyHeader.classList.remove('card-end');
          }
        } else if (card.classList.contains('sticky-header')) {
          card.classList.remove('sticky-header');
          card.querySelector('.quote-header').style.removeProperty('top');
        }
      });
    },

    getIconUrl(propertyValue, isCrm) {
      if (isCrm){
        if (propertyValue === 'Restricted') {
          return '/assets/public/images/icon_restricted_need.svg';
        }
        if (propertyValue === 'Excluded') {
          return '/assets/public/images/icon_excluded_need.svg';
        }
        return '/assets/public/images/icon_included_need.svg';
      }
      else{
        if (propertyValue === 'Restricted') {
          return '/assets/public/images/icons.svg#restricted';
        }
        if (propertyValue === 'Excluded') {
          return '/assets/public/images/icons.svg#excluded';
        }
        return '/assets/public/images/icons.svg#included';
      }
    },

    convertToCurrencyString,
    formatPercentageString,
    stringifyDiscountCategory,
    summaryCoverComposition,
    toSentenceCase,
  },

  watch: {
    selectedExcess() {
      this.$emit('excessUpdated', this.selectedExcess, this.productCardIndex);
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@general/styles/tokens';

.quote-header {
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding: 24px;
  margin: -24px;
}

.recommendation-card {
  @include flex-col;
  --product-card-accent: #{$PRIMARY_MEDIUM_GREEN_100};
  --list-color: #{$PRIMARY_MEDIUM_GREEN_100};
  --discounts-icon: url(/assets/public/images/bullet-icon.svg);

  background: $NEUTRAL_WHITE;
  border: 1px solid $PRIMARY_GREY_40;
  border-radius: $MD_OUTER;
  box-shadow: 0px 1px 2px 0px rgba($NEUTRAL_MIDNIGHT, 0.05);
  flex: 1;
  gap: 1.5rem;
  padding: 24px;
  transition: box-shadow $TRANSITION_TIMING ease, border-color $TRANSITION_TIMING ease;

  &:hover {
    border-color: var(--product-card-accent);
    box-shadow: 0px 2px 6px 0px rgba($NEUTRAL_MIDNIGHT, 0.06), 0px 2px 4px 0px rgba($NEUTRAL_MIDNIGHT, 0.02);
  }

  &:focus-within {
    outline: 2px solid $PRIMARY_GREEN_100;
    outline-offset: 4px;
    border-color: var(--product-card-accent);
  }

  :deep(*) {
    transition: font-size 0.3s ease, height 0.3s ease, opacity 0.3s ease, padding 0.3s ease, gap 0.2s ease;
  }

  .promotional-tag {
    border-radius: $SM_OUTER;
    box-sizing: border-box;
    left: 24px;
    min-width: 11rem;
    padding: 12px;
    position: absolute;
    text-align: center;
    top: -40px;
    transition: opacity 0.1s ease;

    p {
      margin-bottom: 0;
    }
  }

  .blue {
    background-color: $PRIMARY_SKY_100;
    color: $NEUTRAL_MIDNIGHT;
  }

  .deep-green {
    background-color: $PRIMARY_GREEN_100;
    color: $NEUTRAL_WHITE;
  }

  .green {
    background-color: $PRIMARY_MEDIUM_GREEN_100;
    color: $NEUTRAL_WHITE;
  }

  .recommendation-price {
    color: $NEUTRAL_MIDNIGHT;
    font-weight: $BOLD;

    span {
      font-size: 31px;
    }
  }

  .discounts,
  .loadings {
    #disc-load-subheading {
      font-weight: $BOLD;
    }

    ul {
      margin: 0;
      padding: 0;
    }

    li {
      @include flex-row;
      gap: $SM;

      &::before {
        content: '';
        width: $BASE;
        height: $BASE;
        flex: 0 0 $BASE;
        align-self: center;
        background-color: $PRIMARY_GREEN_100;
        mask-image: var(--discounts-icon);
        -webkit-mask-image: var(--discounts-icon);
        mask-size: contain;
        -webkit-mask-size: contain;
        mask-repeat: no-repeat;
        -webkit-mask-repeat: no-repeat;
        mask-position: center;
        -webkit-mask-position: center;
      }

      &:last-child {
        border-bottom: 0;
      }
    }

    .description {
      margin-bottom: 0;
    }

    .description-small {
      margin-bottom: 0;
      font-size: 14px;
    }

    .discount-category {
      padding-left: 0;
    }

    .adjustment-percentage {
      font-weight: $BOLD;
      margin-left: auto;
      color: $PRIMARY_MEDIUM_GREEN_100;
    }

    .adjustment-title {
      max-width: 225px;
    }
  }

  label {
    font-weight: $BOLD;
  }

  .spacer {
    display: none;
  }

  #keep-cover-btn,
  #select-cover-btn,
  #email-quote-btn {
    width: 100%;
  }
}

.middle-card {
  margin: 0 3rem;

  @include mobile {
    margin: 3rem 0;
  }
}

.green-border {
  --product-card-accent: #{$PRIMARY_MEDIUM_GREEN_100};
}

.blue-border {
  --product-card-accent: #{$PRIMARY_SKY_100};
}

.deep-green-border {
  --product-card-accent: #{$PRIMARY_GREEN_100};
}

.product-descriptions {
  transition: unset;
}

.extras-description-wrapper,
.hospital-description-wrapper {
  min-height: auto;
  @include mobile {
    min-height: auto;
  }
}

.sticky-header {
  padding-top: 0;

  .spacer {
    position: sticky;
    display: block;
    background-color: $NEUTRAL_WHITE;
    box-shadow: 0 0 10px 10px $NEUTRAL_WHITE;
    z-index: 10;
    padding: 20px;
    margin-left: -20px;
    min-width: 100%;
    opacity: 100%;
    transition: opacity 0.1s ease;

    &.card-end {
      opacity: 0%;
      transition: opacity 0.3s ease;
    }
  }

  .quote-header {
    position: sticky;
    padding-top: 17px;
    padding-bottom: 0px;
    margin-bottom: 34px;
    gap: 0;
    border-radius: $MD_OUTER $MD_OUTER 0 0;
    background-color: $NEUTRAL_WHITE;
    border: 1px solid $PRIMARY_GREY_20;
    border-bottom: 0;
    box-shadow: 0px 2px 6px 0px rgba($NEUTRAL_MIDNIGHT, 0.06), 0px 2px 4px 0px rgba($NEUTRAL_MIDNIGHT, 0.02);
    z-index: 10;

    .promotional-tag {
      opacity: 0%;
    }

    .recommendation-card-heading h2 {
      font-size: $DESKTOP_H5_SIZE;
      @include mobile {
        font-size: $MOBILE_H5_SIZE;
      }
    }

    .recommendation-price {
      margin-bottom: 0;

      .h2 {
        font-size: 27px;
      }
    }

    .cover-selection-wrapper a.button {
      margin-bottom: 0;
    }
  }
}
</style>